import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useWalletTransactions(dateRange = { fromDate: '', toDate: '' }) {
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const { send, isConnected, isOpen, connect, registerHandler } = useWebSocketContext();
    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    const groupId = localStorage.getItem("group_id");
    const roleId = localStorage.getItem("role_id");

    const fetchTransactions = useCallback(async (attempt = 0) => {
        if (!mountedRef.current) return;

        setIsLoading(true);
        setError('');
        setMessage(''); 

        try {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            if (!isConnected) {
                await connect();

                await new Promise((resolve, reject) => {
                    const start = Date.now();
                    const checkInterval = setInterval(() => {
                        if (!mountedRef.current) {
                            clearInterval(checkInterval);
                            reject(new Error('Component unmounted'));
                        } else if (isOpen()) {
                            clearInterval(checkInterval);
                            resolve();
                        } else if (Date.now() - start > 5000) {
                            clearInterval(checkInterval);
                            reject(new Error('Connection timeout'));
                        }
                    }, 100);
                });
            }

            // Default to last 7 days if fromDate/toDate not provided
            const fromDate = dateRange.fromDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            const toDate = dateRange.toDate || new Date().toISOString().split('T')[0];

            const payload = {
                group_id: groupId,
                role_id: roleId,
                from_date: fromDate,
                to_date: toDate,
            };

            const response = await send('cms_get_wallet_transactions', payload);
            console.log('Wallet Transactions response:', response);

            if (!response || response.type !== 'cms_get_wallet_transactions_response') {
                throw new Error('Invalid response from server');
            }

            if (!Array.isArray(response.data)) {
                throw new Error('Server did not return a valid wallet transactions list');
            }

            setTransactions(response.data);
            setMessage(response.message || '');
        } catch (err) {
            setError(err.message);

            if (mountedRef.current) {
                const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
                retryTimeoutRef.current = setTimeout(() => {
                    fetchTransactions(attempt + 1);
                }, delay);
            }
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [isConnected, isOpen, connect, send, groupId, roleId, dateRange]);

    useEffect(() => {
        mountedRef.current = true;
        fetchTransactions();

        const unregister = registerHandler('wallet_transactions_update', (msg) => {
            if (Array.isArray(msg.data) && mountedRef.current) {
                setTransactions(msg.data);
            }
        });

        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            unregister();
        };
    }, [fetchTransactions, registerHandler]);

    return { transactions, isLoading, error, message, refetch: () => fetchTransactions(0) };
}
